// Copyright (c) Ping. All rights reserved.
//
// GIF picker popover. Pixel-pinned to Figma `2804:27348` "GIF
// container" — the tabbed emoji popover with the GIFs tab active
// (see `tmp/figma-chatroom-redo/emoji-gif-extracted.md` for the
// jq-derived measurement table). Pure controlled view: host owns
// `query` + `items` + tab dispatch.
//
// Critical structural note: the GIF container in Figma is the SAME
// popover as the emoji container — both share an identical
// `Emoji / GIFs / Stickers` tab bar at the top. There is NO
// independent GIF trigger button in the composer. The unified emoji
// overlay (`ComposerEmojiOverlay`) renders the inner `GifPanel`
// below directly when its internal `tab === 'gifs'`. The exported
// `ComposerGifOverlay` is the standalone wrapper retained for tests
// + parity with the iOS contract.
//
// Layout per Figma `2804:27348`:
//   - Outer: 370 × 456, radius 16, fill `#11131A` (surface.primary)
//   - Padding: 16 all sides
//   - itemSpacing: 16 (between tab-bar / search / grid)
//   - Search input `2804:27355`: 338×40, paddings 10/10/10/10,
//     radius 12, fill `#222429`. Placeholder `Search GIF`.
//   - Grid `2804:27362`: 321w, 3 columns × 105×100 thumbnails,
//     3px row gap, 3px column gap. Aspect ratio ≈ 1.05.

import React, { useMemo } from 'react';
import { FlatList, Platform, Pressable, StyleSheet, Text, type TextStyle, TextInput, View } from 'react-native';
import { useColorScheme } from '../../foundations/colorScheme';
import { tokens } from '../../design-system/tokens';
import type { GifItem } from './GifSearchProvider';

export type { GifItem };

export interface ComposerGifOverlayProps {
  readonly open: boolean;
  readonly items: readonly GifItem[];
  readonly query: string;
  readonly onChangeQuery: (q: string) => void;
  readonly onSelect: (gif: GifItem) => void;
  readonly onClose: () => void;
  readonly testID?: string | undefined;
}

// Figma `2804:27363`: 3 cells per row, 3px gaps. The grid frame
// width inside the popover (`2804:27362`) is 321 because the
// popover's inner content area is 338 less the scroll inset; for
// our flex layout we let the FlatList compute cell width from
// `numColumns` + flex inside its own column wrapper. Aspect ratio
// is fixed to 105/100 = 1.05 so the cell height tracks the width.
const GRID_COLUMNS = 3;
const GRID_GAP = 3;
const THUMB_ASPECT = 105 / 100;

/**
 * Standalone GIF popover — Figma `2804:27348`. Renders the full
 * popover chrome (outer 370×456 frame with radius/padding/border)
 * around an inner `<GifPanel>`. Used by hosts that want a GIF-only
 * picker without the tab bar; the unified emoji overlay composes
 * `<GifPanel>` directly when its internal `tab === 'gifs'`.
 */
export const ComposerGifOverlay = React.memo<ComposerGifOverlayProps>(
  ({ open, items, query, onChangeQuery, onSelect, onClose, testID }) => {
    const scheme = useColorScheme();
    const colorScheme: 'light' | 'dark' = scheme === 'dark' ? 'dark' : 'light';
    const colors = tokens.color.semantic[colorScheme];

    if (!open) return null;

    return (
      <View
        // Figma `2804:27348` — 370×456 outer popover, hairline border.
        className="w-[370px] max-w-full h-[456px] rounded-lg p-lg flex flex-col gap-lg border"
        style={{
          backgroundColor: colorScheme === 'dark' ? '#111318' : colors.surface.primary,
          borderColor: colorScheme === 'dark' ? '#2F333B' : colors.border.secondary,
          borderWidth: StyleSheet.hairlineWidth,
        }}
        testID={testID}
      >
        <GifPanel
          items={items}
          query={query}
          onChangeQuery={onChangeQuery}
          onSelect={onSelect}
          onClose={onClose}
        />
      </View>
    );
  },
);

ComposerGifOverlay.displayName = 'ComposerGifOverlay';

interface GifPanelProps {
  readonly items: readonly GifItem[];
  readonly query: string;
  readonly onChangeQuery: (q: string) => void;
  readonly onSelect: (gif: GifItem) => void;
  readonly onClose: () => void;
  /** Search placeholder copy — defaults to `Search GIF`. */
  readonly searchPlaceholder?: string | undefined;
}

/**
 * Inner panel — search input + grid. Renders WITHOUT the outer
 * popover chrome so it can be embedded inside the unified emoji
 * popover when the user is on the GIFs tab.
 */
export const GifPanel: React.FC<GifPanelProps> = ({
  items,
  query,
  onChangeQuery,
  onSelect,
  onClose,
  searchPlaceholder,
}) => {
  const scheme = useColorScheme();
  const colorScheme: 'light' | 'dark' = scheme === 'dark' ? 'dark' : 'light';
  const colors = tokens.color.semantic[colorScheme];

  // Pre-chunk into rows of GRID_COLUMNS so we can render a
  // VirtualizedList without re-rendering the entire grid on each
  // `query` change. Each row gets a stable key (`gif-row-${rowIdx}`)
  // and the cells inside it are positioned with flex + 3px gap.
  const rows = useMemo<GifItem[][]>(() => {
    const out: GifItem[][] = [];
    for (let i = 0; i < items.length; i += GRID_COLUMNS) {
      out.push(items.slice(i, i + GRID_COLUMNS) as GifItem[]);
    }
    return out;
  }, [items]);

  const placeholderColor = colorScheme === 'dark' ? '#B5BAC3' : colors.text.tertiary;
  const inputBg = colorScheme === 'dark' ? '#222429' : colors.surface.secondary;
  const cellBg = colorScheme === 'dark' ? '#222429' : colors.surface.secondary;

  return (
    <View className="flex-1 flex flex-col gap-lg">
      {/*
        Figma `2804:27355` — 338×40 search input, padding 12, radius 12.
      */}
      <View className="flex flex-row items-center gap-sm pt-lg">
        <TextInput
          className="
            flex-1 h-10 px-md py-0 rounded-[12px]
            font-sans text-[14px] leading-5
          "
          style={[
            { color: colors.text.primary, backgroundColor: inputBg },
            // `outlineStyle` is a web-only CSS prop; RN-macOS rejects it
            // ("Invalid props.style key `outlineStyle`"), so gate it on web.
            ...(Platform.OS === 'web'
              ? [{ outlineStyle: 'none' } as unknown as TextStyle]
              : []),
          ]}
          value={query}
          onChangeText={onChangeQuery}
          placeholder={searchPlaceholder ?? 'Search GIF'}
          placeholderTextColor={placeholderColor}
          accessibilityLabel="Search GIFs"
        />
        <Pressable
          onPress={onClose}
          accessibilityRole="button"
          accessibilityLabel="Close GIF picker"
        >
          <Text
            className="font-sans text-[14px] font-medium px-xs"
            style={{ color: colors.text.secondary }}
          >
            Done
          </Text>
        </Pressable>
      </View>

      {items.length === 0 ? (
        <Text
          className="font-sans text-[14px] text-center py-xxl"
          style={{ color: colors.text.tertiary }}
        >
          {query.trim().length === 0
            ? 'No trending GIFs available.'
            : 'No GIFs found.'}
        </Text>
      ) : (
        <FlatList
          data={rows}
          keyExtractor={(_row, idx) => `gif-row-${idx}`}
          renderItem={({ item: row }) => (
            // Figma `2804:27363` — horizontal row of 3 cells, 3px gap.
            // `gap: GRID_GAP` stays inline because the gap value is a
            // module constant; NativeWind's arbitrary `gap-[3px]` works
            // but tokenizes oddly under DTW.
            <View className="flex flex-row" style={{ gap: GRID_GAP }}>
              {row.map((gif) => (
                <Pressable
                  key={gif.id}
                  onPress={() => onSelect(gif)}
                  accessibilityRole="button"
                  accessibilityLabel={gif.altText ?? `Send GIF ${gif.id}`}
                  className="flex-1 rounded-sm overflow-hidden"
                  style={{
                    aspectRatio: THUMB_ASPECT,
                    backgroundColor: cellBg,
                  }}
                  testID={`composer-gif-cell-${gif.id}`}
                >
                  <CellThumbnail uri={gif.previewUrl} altText={gif.altText} />
                </Pressable>
              ))}
              {row.length < GRID_COLUMNS
                ? Array.from({ length: GRID_COLUMNS - row.length }).map((_, padIdx) => (
                    <View
                      // biome-ignore lint/suspicious/noArrayIndexKey: padding cell
                      key={`pad-${padIdx}`}
                      className="flex-1"
                      style={{ aspectRatio: THUMB_ASPECT }}
                    />
                  ))
                : null}
            </View>
          )}
          contentContainerStyle={{ paddingBottom: 4, gap: GRID_GAP }}
          showsVerticalScrollIndicator={false}
        />
      )}
    </View>
  );
};

// Thumbnail renderer. On `react-native-web` (apps/web) the host
// passes an https URL from Tenor; we render it through a plain
// `<img>` tag for free GIF playback. On native we fall back to a
// solid background — host-side adapters can swap in `<Image>` once
// a mobile flow exists. The wrapper keeps a fixed aspectRatio so
// the parent row stays at 100 tall.
interface CellThumbnailProps {
  readonly uri: string;
  readonly altText?: string | undefined;
}

const CellThumbnail: React.FC<CellThumbnailProps> = ({ uri, altText }) => {
  // react-native-web exposes the global `document`; on native we
  // fall back to a plain View. The `as any` cast is needed because
  // TS doesn't include `img` in the React Native types. Inline
  // styles stay on the `<img>` because plain DOM elements can't
  // consume NativeWind classNames.
  if (typeof document !== 'undefined') {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const ImgTag: any = 'img';
    return (
      <ImgTag
        src={uri}
        alt={altText ?? ''}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          borderRadius: 8,
          display: 'block',
        }}
      />
    );
  }
  return (
    <View
      className="w-full h-full rounded-sm"
      style={{ backgroundColor: '#222429' }}
    />
  );
};
